<template>
    <section class="mx-auto mt-12 max-w-4xl border-t border-gray-200 dark:border-default pt-8" aria-label="Reakcie a komentáre">
        <h2 class="text-xl font-bold text-gray-900 dark:text-txt-headings">Ako sa vám páčil článok?</h2>
        <div class="mt-4 flex flex-wrap gap-2">
            <button
                v-for="reaction in BLOG_REACTIONS"
                :key="reaction.type"
                type="button"
                class="rounded-full border px-4 py-2 text-sm transition-colors disabled:opacity-50"
                :class="
                    engagement?.myReaction === reaction.type
                        ? 'border-primary-600 bg-primary-50 text-primary-700 dark:border-primary-300 dark:bg-primary-950 dark:text-primary-100'
                        : 'border-gray-200 dark:border-default hover:bg-gray-50 dark:hover:bg-subtle'
                "
                :aria-label="`${reaction.label}: ${engagement?.reactions[reaction.type] || 0}`"
                :aria-pressed="engagement?.myReaction === reaction.type"
                :title="reaction.label"
                :disabled="!!busy || loading || !engagement"
                @click="react(reaction.type)"
            >
                <span aria-hidden="true">{{ reaction.emoji }}</span> {{ engagement?.reactions[reaction.type] || 0 }}
            </button>
        </div>

        <h2 class="mt-8 text-xl font-bold text-gray-900 dark:text-txt-headings">
            Komentáre <span v-if="engagement">({{ engagement.commentCount }})</span>
        </h2>
        <p v-if="!isSignedIn" class="mt-4 text-sm text-gray-600 dark:text-txt-body">
            Chcete reagovať alebo pridať komentár?
            <button type="button" class="font-medium text-primary-600 dark:text-txt-brand-action underline" @click="openAuthModal('login')">
                Prihláste sa</button
            >.
        </p>
        <form v-else class="mt-4" @submit.prevent="createComment">
            <label :for="`blog-comment-${postId}`" class="block text-sm font-medium text-gray-700 dark:text-txt-body">Váš komentár</label>
            <textarea
                :id="`blog-comment-${postId}`"
                v-model="draft"
                class="mt-2 w-full rounded-lg border border-gray-300 dark:border-default dark:bg-surface-card dark:text-txt-body dark:placeholder:text-txt-caption p-3 focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-200"
                rows="3"
                :maxlength="BLOG_COMMENT_MAX_LENGTH"
                placeholder="Podeľte sa o svoj názor..."
                :disabled="!!busy"
                required
            />
            <div class="mt-2 flex items-center justify-between gap-3">
                <span class="text-xs text-gray-500 dark:text-txt-caption">{{ draft.length }}/{{ BLOG_COMMENT_MAX_LENGTH }}</span>
                <button
                    type="submit"
                    class="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                    :disabled="!!busy || loading || !engagement || !draft.trim()"
                >
                    {{ busy === 'create' ? 'Odosielam...' : 'Pridať komentár' }}
                </button>
            </div>
        </form>

        <p v-if="error" role="alert" class="mt-4 text-sm text-red-600 dark:text-red-300">{{ error }}</p>
        <button
            v-if="!engagement && !loading"
            type="button"
            class="mt-3 text-sm text-primary-600 dark:text-txt-brand-action underline"
            @click="load()"
        >
            Skúsiť znova
        </button>
        <p v-if="loading" role="status" class="mt-6 text-sm text-gray-500 dark:text-txt-caption">Načítavam komentáre...</p>
        <p v-if="engagement && !engagement.commentCount" class="mt-6 text-sm text-gray-500 dark:text-txt-caption">
            Zatiaľ tu nie sú žiadne komentáre. Buďte prvý!
        </p>

        <div v-if="engagement" class="mt-6 space-y-5">
            <article
                v-for="comment in engagement.comments"
                :key="comment.id"
                class="rounded-xl border border-gray-200 dark:border-default p-4"
            >
                <div class="flex flex-wrap items-center justify-between gap-2">
                    <p class="font-medium text-gray-900 dark:text-txt-headings">{{ comment.authorName }}</p>
                    <p class="text-xs text-gray-500 dark:text-txt-caption">
                        <time :datetime="comment.createdAt">{{ formatDate(comment.createdAt) }}</time
                        ><span v-if="comment.updatedAt"> · upravené</span>
                    </p>
                </div>
                <form v-if="editingId === comment.id" class="mt-3" @submit.prevent="saveComment(comment.id)">
                    <label :for="`edit-comment-${comment.id}`" class="sr-only">Upraviť komentár</label>
                    <textarea
                        :id="`edit-comment-${comment.id}`"
                        v-model="editDraft"
                        rows="3"
                        class="w-full rounded-lg border border-gray-300 dark:border-default dark:bg-surface-card dark:text-txt-body dark:placeholder:text-txt-caption p-3"
                        :maxlength="BLOG_COMMENT_MAX_LENGTH"
                        :disabled="!!busy"
                        required
                    />
                    <div class="mt-2 flex gap-4 text-sm">
                        <button
                            type="submit"
                            class="font-medium text-primary-600 dark:text-txt-brand-action disabled:opacity-50"
                            :disabled="!!busy || !editDraft.trim()"
                        >
                            Uložiť
                        </button>
                        <button type="button" class="text-gray-600 dark:text-txt-body" :disabled="!!busy" @click="editingId = null">
                            Zrušiť
                        </button>
                    </div>
                </form>
                <p v-else class="mt-3 whitespace-pre-wrap break-words text-gray-700 dark:text-txt-body">{{ comment.content }}</p>
                <div
                    v-if="store.userId && (comment.userId === store.userId || engagement.canModerate)"
                    class="mt-3 flex gap-4 text-sm"
                >
                    <button
                        v-if="comment.userId === store.userId && editingId !== comment.id"
                        type="button"
                        class="text-primary-600 dark:text-txt-brand-action disabled:opacity-50"
                        :disabled="!!busy"
                        @click="startEditing(comment)"
                    >
                        Upraviť
                    </button>
                    <button
                        type="button"
                        class="text-red-600 dark:text-red-300 disabled:opacity-50"
                        :disabled="!!busy"
                        @click="deleteComment(comment.id)"
                    >
                        {{ busy === comment.id ? 'Ukladám...' : 'Vymazať' }}
                    </button>
                </div>
            </article>
        </div>
        <button
            v-if="engagement?.nextCursor"
            type="button"
            class="mt-5 text-sm font-medium text-primary-600 dark:text-txt-brand-action disabled:opacity-50"
            :disabled="loading || !!busy"
            @click="load(true)"
        >
            Načítať ďalšie komentáre
        </button>
    </section>
</template>

<script setup lang="ts">
import {
    BLOG_REACTIONS,
    BLOG_COMMENT_MAX_LENGTH,
    type BlogReactionType,
    type BlogComment,
    type BlogEngagement
} from '#shared/types/BlogEngagement'
import { useApplicationStore } from '@/stores/application'

const props = defineProps<{ slug: string; postId: string }>()
const store = useApplicationStore()
const { authFetch } = useAuthFetch()
const { openAuthModal } = useAuthModal()
const engagement = ref<BlogEngagement | null>(null)
const loading = ref(true)
const busy = ref<string | null>(null)
const error = ref('')
const draft = ref('')
const editingId = ref<string | null>(null)
const editDraft = ref('')
const endpoint = computed(() => `/api/blogs/${encodeURIComponent(props.slug)}`)
// The server has no auth state, so render the signed-out prompt until hydration is done.
const isMounted = ref(false)
const isSignedIn = computed(() => isMounted.value && !!store.userId)
let generation = 0
onBeforeUnmount(() => {
    generation++
})

onMounted(() => {
    isMounted.value = true
    watch(
        () => [props.slug, store.userId],
        () => {
            generation++
            engagement.value = null
            editingId.value = null
            editDraft.value = ''
            draft.value = ''
            busy.value = null
            void load()
        },
        { immediate: true }
    )
})

function formatDate(date: string) {
    return new Intl.DateTimeFormat('sk-SK', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'Europe/Bratislava'
    }).format(new Date(date))
}

function showFailure(cause: unknown) {
    const status =
        (cause as { statusCode?: number; status?: number }).statusCode || (cause as { status?: number }).status
    error.value =
        status === 429
            ? 'Pred ďalším komentárom počkajte 15 sekúnd.'
            : status === 401
              ? 'Pre túto akciu sa prihláste znova.'
              : 'Akciu sa nepodarilo dokončiť. Skúste to znova.'
}

async function load(more = false) {
    const current = generation
    loading.value = true
    error.value = ''
    try {
        const result = await authFetch<BlogEngagement>(`${endpoint.value}/engagement`, {
            query: more ? { cursor: engagement.value?.nextCursor } : undefined
        })
        if (current !== generation) return
        if (more && engagement.value) {
            const existing = new Set(engagement.value.comments.map((comment) => comment.id))
            result.comments = [
                ...engagement.value.comments,
                ...result.comments.filter((comment) => !existing.has(comment.id))
            ]
        }
        engagement.value = result
    } catch (cause) {
        if (current === generation) showFailure(cause)
    } finally {
        if (current === generation) loading.value = false
    }
}

async function mutate(key: string, action: (url: string) => Promise<void>) {
    if (!store.userId) {
        openAuthModal('login')
        return
    }
    if (busy.value || loading.value) return
    const current = generation
    busy.value = key
    error.value = ''
    try {
        await action(endpoint.value)
    } catch (cause) {
        if (current === generation) showFailure(cause)
    } finally {
        if (current === generation) busy.value = null
    }
}

async function react(type: BlogReactionType) {
    const current = generation
    await mutate('reaction', async (url) => {
        const result = await authFetch<Pick<BlogEngagement, 'reactions' | 'myReaction'>>(`${url}/reaction`, {
            method: 'PUT',
            body: { type: engagement.value?.myReaction === type ? null : type }
        })
        if (current === generation && engagement.value) Object.assign(engagement.value, result)
    })
}

async function createComment() {
    if (!draft.value.trim()) return
    const current = generation
    await mutate('create', async (url) => {
        const comment = await authFetch<BlogComment>(`${url}/comments`, {
            method: 'POST',
            body: { content: draft.value }
        })
        if (current !== generation || !engagement.value) return
        engagement.value.comments.unshift(comment)
        engagement.value.commentCount++
        draft.value = ''
    })
}

function startEditing(comment: BlogComment) {
    editingId.value = comment.id
    editDraft.value = comment.content
}

async function saveComment(id: string) {
    if (!editDraft.value.trim()) return
    const current = generation
    await mutate(id, async (url) => {
        const updated = await authFetch<BlogComment>(`${url}/comments/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: { content: editDraft.value }
        })
        if (current !== generation || !engagement.value) return
        engagement.value.comments = engagement.value.comments.map((comment) => (comment.id === id ? updated : comment))
        editingId.value = null
    })
}

async function deleteComment(id: string) {
    if (!window.confirm('Naozaj chcete vymazať tento komentár?')) return
    const current = generation
    await mutate(id, async (url) => {
        await authFetch(`${url}/comments/${encodeURIComponent(id)}`, { method: 'DELETE' })
        if (current !== generation || !engagement.value) return
        engagement.value.comments = engagement.value.comments.filter((comment) => comment.id !== id)
        engagement.value.commentCount = Math.max(0, engagement.value.commentCount - 1)
        if (editingId.value === id) editingId.value = null
    })
}
</script>
